/**
 * THE SHELL every place is drawn in, from the artboards in `faithfully-kept-business/design/home/`:
 * the header with its Call button, and on every signed-in place its Account button and the name
 * as a link to Home (`Header.tsx`, slice A1), the page, and the bottom nav with the place shown
 * marked, or none on Account's pages. The page's padding and the space between its cards are the
 * artboards' own, 24px above, 20px at the sides and between cards, and 32px below. The page is
 * drawn in the one column (`column.ts`): the whole width on a phone, as drawn, and one centred
 * column at a desk, with the header, the nav and whatever sits on it running the whole width around
 * it, their rows in the column too.
 *
 * `over` is what is drawn over the page, such as Schedule's skip or move sheet. While it is there,
 * the header, the page and the nav are inert: nothing behind it can be reached by a tap, a key or a
 * screen reader, so the sheet is the one thing to act on until it closes.
 *
 * `foot` is what sits on the nav, at the foot of the screen with it while the page scrolls, as
 * Messages draws its composer: the field to write in stays under the thumb however long the thread.
 *
 * `payments` is whether the person pays here, as the place's household says (`HouseholdShown`):
 * only then does the nav offer Payments.
 *
 * COVERING THE SCREEN BELOW THE HEADER (`useCover`): what the page draws over itself from deep in
 * it, the panel while she is taken to Stripe's page (`StripeWay.tsx`), makes the page and the nav
 * inert while it is there, and the header's name and Account with them, never its Call, which stays
 * one tap away above the dim. The header is drawn above anything so drawn (`COVER_LAYER`), and
 * under a sheet, which dims it too. The page and the nav are made inert at once, as the panel comes
 * and goes, rather than on the Shell's next render, so the focus a screen gives as it changes never
 * lands on what is still inert.
 *
 * THE NAV'S MARKS (provisional P49) come from the household of the place shown, which `Guarded`
 * hands down from the place's own read (`ShownHousehold`), so every place marks the nav with what
 * its read said, and no screen passes them; a shell drawn with no place read, as for a read that did
 * not go through, marks nothing (`lib/marks.ts`). With the household comes the place's view of the
 * payments on their way (`useOnItsWay`, slice V2), which the count leaves out, as the screen does.
 */
import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react';
import type { HouseholdShown } from '../api.ts';
import { navMarks } from '../lib/marks.ts';
import { onItsWay } from '../lib/paying.ts';
import { BottomNav, type Tab } from './BottomNav.tsx';
import { COLUMN } from './column.ts';
import { Header } from './Header.tsx';

/** The layer what covers the screen below the header is drawn in: over the nav, under the header. */
export const COVER_LAYER = 'z-15';

/** Makes the page and the nav inert, or not, for what covers them. */
type Cover = (covering: boolean) => void;

const CoverContext = createContext<Cover>(() => undefined);

/** Covers the page and the nav, never the header, while what the page draws over them is there. */
export function useCover(): Cover {
  return useContext(CoverContext);
}

/** The household of the place shown, as its read gave it, or none where nothing was read. */
const HouseholdContext = createContext<HouseholdShown | undefined>(undefined);

/** Whether an invoice is on its way, as the place shown draws it. */
type OnItsWay = (invoiceId: string) => boolean;

/**
 * Whether an invoice is on its way, from the place shown's view of the payments on their way her
 * browser keeps (`Guarded`, `lib/paying.ts`, P35), and, with no place shown, from what her browser
 * keeps now.
 */
const OnItsWayContext = createContext<OnItsWay>(onItsWay);

/**
 * Whether an invoice is on its way, as the place shown draws it: what every Pay held back, the nav's
 * count and Home's line read, so all of them change together, when the place's view of the payments
 * on their way changes.
 */
export function useOnItsWay(): OnItsWay {
  return useContext(OnItsWayContext);
}

/**
 * Hands the household of the place shown down to its shell, which marks the nav from it, and the
 * place's view of the payments on their way, which the shell and the screen draw from. A screen that
 * hands down its household again, as Home does, gives no view, and the place's still holds.
 */
export function ShownHousehold({
  household,
  onItsWay: given,
  children,
}: {
  readonly household: HouseholdShown | undefined;
  readonly onItsWay?: OnItsWay;
  readonly children: ReactNode;
}) {
  const outer = useContext(OnItsWayContext);
  const held = given ?? outer;
  return (
    <HouseholdContext value={household}>
      <OnItsWayContext value={held}>{children}</OnItsWayContext>
    </HouseholdContext>
  );
}

interface ShellProps {
  /** The tab the place is, or null on a page that is none of the four, as Account is. */
  readonly at: Tab | null;
  readonly children: ReactNode;
  /** Drawn over the page, which is inert while it is there. */
  readonly over?: ReactNode;
  /** Drawn on the nav, and kept at the foot of the screen with it. */
  readonly foot?: ReactNode;
  /** Whether the person pays here, so the nav offers Payments. */
  readonly payments?: boolean;
}

export function Shell({ at, children, over, foot, payments }: ShellProps) {
  const covered = over !== undefined && over !== null && over !== false;
  const marks = navMarks(useContext(HouseholdContext), useContext(OnItsWayContext));
  /** The page and the nav, laid out as if this box were not there. */
  const below = useRef<HTMLDivElement>(null);
  /** The header, laid out the same way, whose name and Account are covered with the page. */
  const top = useRef<HTMLDivElement>(null);
  const cover = useCallback<Cover>((covering) => {
    below.current?.toggleAttribute('inert', covering);
    for (const coverable of top.current?.querySelectorAll('[data-coverable]') ?? []) {
      coverable.toggleAttribute('inert', covering);
    }
  }, []);
  return (
    <CoverContext value={cover}>
      <div className="bg-surface-page">
        <div className="flex min-h-dvh flex-col" inert={covered}>
          <div ref={top} className="contents">
            <Header signedIn />
          </div>
          <div ref={below} className="contents">
            <main className={`${COLUMN} flex flex-1 flex-col gap-5 px-5 pt-6 pb-8`}>
              {children}
            </main>
            {foot ? (
              <div className="sticky bottom-0 z-10">
                {foot}
                <BottomNav at={at} payments={payments} marks={marks} />
              </div>
            ) : (
              <BottomNav at={at} payments={payments} marks={marks} />
            )}
          </div>
        </div>
        {covered ? over : null}
      </div>
    </CoverContext>
  );
}
